/* import __COLOCATED_TEMPLATE__ from './input-autocomplete.hbs'; */
import Component from '@ember/component';
import { isPresent, isEmpty } from '@ember/utils';
const KEY_CODE_ENTER = 13;
const KEY_CODE_ESC = 27;
const KEY_CODE_UP = 38;
const KEY_CODE_DOWN = 40;

// Chrome overrides the "off" input and continues to show the native autocomplete dropdown.
// Online forums suggest using "new-password" to stop autocomplete because this is
// typically used in new account forms to avoid autofilling other existing passwords.
const AUTOCOMPLETE_OFF = 'new-password';

/**
 * This component is a wrapper around a market-field and market-input-text component.
 * It supports fieldkit formatters and showing validation errors.
 *
 * ```
 * <Prism::InputAutocomplete
 *   @value={{this.nameOfValueProperty}}
 *   @label="Favorite Fruit"
 *   @options={{array 'Apple' 'Orange' 'Banana'}}
 *   @onChange={{action (mut nameOfValueProperty))}}
 * />
 * ```
 *
 */
export default Component.extend({
  tagName: '',
  /**
   * Whether the browser can autocomplete the input.
   *
   * @argument inputBrowserAutocomplete
   * @type {String}
   */
  autocomplete: AUTOCOMPLETE_OFF,
  /**
   * @param {String} inputType - "type" to apply to the underlying input field
   */
  inputType: 'text',
  /**
   * @param {String} tooltipText - Text to populate a tooltip, only appears if this is passed in
   */
  tooltipText: null,
  /**
   * Suggestions to yield from the dropdown.
   *
   * @argument suggestions
   * @type {Array<any>}
   */
  suggestions: null,
  /**
   * The class to be assigned to the suggestions box.
   *
   * @argument suggestionsClass
   * @type {String}
   */
  suggestionsClass: '',
  /**
   * Action to call when a suggestion is selected. The suggestion object will be passed in as a parameter.
   *
   * @argument selectSuggestionAction
   * @type {Action}
   */
  selectSuggestionAction() {},
  /**
   * Action to call when the input is changed.
   *
   * @argument onChange
   * @type {Action}
   */
  onChange() {},
  highlightIndex: null,
  showSuggestions: false,
  selectSuggestion(...args) {
    this.selectSuggestionAction(...args);
    this.set('showSuggestions', false);
    this.set('highlightIndex', null);
  },
  highlightNext() {
    this.set('showSuggestions', true);
    if (this.highlightIndex === null) {
      this.set('highlightIndex', 0);
    } else {
      this.set('highlightIndex', Math.min(this.highlightIndex + 1, this.suggestions.length - 1));
    }
  },
  highlightPrevious() {
    this.set('showSuggestions', true);
    if (this.highlightIndex === null) {
      this.set('highlightIndex', this.suggestions.length - 1);
    } else {
      this.set('highlightIndex', Math.max(this.highlightIndex - 1, 0));
    }
  },
  enterPressed(e) {
    if (this.showSuggestions && isPresent(this.highlightIndex)) {
      this.selectSuggestion(this.suggestions[this.highlightIndex]);
      e.stopPropagation();
      e.preventDefault();
    }
  },
  escapePressed(e) {
    e.stopPropagation();
    e.preventDefault();
    this.set('highlightIndex', null);
    this.send('inputFocusOutAction');
  },
  actions: {
    suggestionClickAction(suggestion) {
      this.selectSuggestion(suggestion);
    },
    mouseLeaveAction() {
      this.set('highlightIndex', null);
    },
    keydownAction(e) {
      if (isEmpty(this.suggestions)) {
        return;
      }
      switch (e.keyCode) {
        case KEY_CODE_DOWN:
          this.highlightNext(e);
          break;
        case KEY_CODE_UP:
          this.highlightPrevious(e);
          break;
        case KEY_CODE_ENTER:
          this.enterPressed(e);
          break;
        case KEY_CODE_ESC:
          this.escapePressed(e);
          break;
        default:
          this.set('highlightIndex', null);
          this.set('showSuggestions', true);
      }
    },
    inputFocusInAction() {
      this.set('showSuggestions', true);
    },
    inputFocusOutAction() {
      this.set('showSuggestions', false);
      this.set('highlightIndex', null);
    },
    _onChange(value, event) {
      this.set('showSuggestions', true);
      if (this.onChange) {
        this.onChange(value, event);
      }
    }
  }
});